import dayjs from "dayjs"
import jsPDF from "jspdf"
import autoTable, { RowInput } from "jspdf-autotable"
import { renderToStaticMarkup } from "react-dom/server"
import { SvgZoeeGradientLogo } from "../../../../components/icons/logos/zoee-gradient-logo"

// ——— Helpers ————————————————————————————————————————————————————————————
function hexToRgbArray(hex: string): [number, number, number] {
  const m = hex
    .replace("#", "")
    .match(/^([a-f0-9]{2})([a-f0-9]{2})([a-f0-9]{2})$/i)
  if (!m) return [0, 0, 0]
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
}

async function svgToPngBase64(svgMarkup: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    const svg = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(svg)
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")!
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL("image/png"))
    }
    img.src = url
  })
}

const STATUS_COLORS: Record<string, string> = {
  paid: "#27A745",
  scheduled: "#C79300",
  overdue: "#D53C3C",
  refunded: "#3A7AFE",
  denied: "#6B7280",
}

function computeStatus(t: any): { label: string; hex: string } {
  const raw = String(t.payment_status || t.status || "").toLowerCase()
  const amountNum = Number(t.amount ?? 0)
  const isPast =
    t.date_scheduled && dayjs(t.date_scheduled).isBefore(dayjs(), "day")

  const overdueStatuses = ["declined", "denied"]

  if (overdueStatuses.includes(raw)) {
    return { label: "Overdue", hex: STATUS_COLORS.overdue }
  }

  if (raw === "scheduled" && amountNum > 0 && isPast) {
    return { label: "Overdue", hex: STATUS_COLORS.overdue }
  }

  if (raw === "scheduled" && amountNum <= 0) {
    return { label: "Paid", hex: STATUS_COLORS.paid }
  }

  if (STATUS_COLORS[raw]) {
    return {
      label: raw[0].toUpperCase() + raw.slice(1),
      hex: STATUS_COLORS[raw],
    }
  }

  return { label: "Unknown", hex: STATUS_COLORS.denied }
}

function formatMoney(v: any): string {
  if (v === null || v === undefined || v === "") return "—"
  const n = Number(v)
  if (Number.isNaN(n)) return "—"
  return `$${n.toFixed(2)}`
}

// Same rules as invoice
function formatPaymentMethod(method: any, amount: any): string {
  const hasAmount =
    amount !== null && amount !== undefined && Number(amount) > 0
  if (!hasAmount) return "—"
  if (!method) return "—"

  const m = String(method)
  const clean = m.replace(/\s+/g, "").toLowerCase()

  if (/^\d{4}$/.test(clean)) return `•••• ${m.slice(-4)}`
  if (/^\d{12,19}$/.test(clean)) return `•••• ${clean.slice(-4)}`
  if (clean.includes("paypal")) return "PayPal"
  if (m.includes("•") || /last4/i.test(m)) return m
  return m
}

// ——— Main ————————————————————————————————————————————————————————————————
export default async function generateTransactionsReportPDF(
  subxPayments: any[],
  coachPurchases: any[],
  user: { firstName?: string; lastName?: string; user_id?: string },
  title: string = "Payment History"
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const brand = "#5B6EF5"
  const brandRgb = hexToRgbArray(brand)
  const today = dayjs().format("M/D/YYYY")

  // Footer logo
  const logoBase64 = await svgToPngBase64(
    renderToStaticMarkup(<SvgZoeeGradientLogo />)
  )

  // Header bar
  doc.setFillColor(brand)
  doc.rect(0, 0, pageWidth, 20, "F")
  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor("#FFFFFF")
  doc.text(title, pageWidth / 2, 13, { align: "center" })

  // Sub-header (name + ID + date)
  const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim()
  if (userName) {
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor("#444")
    doc.text(userName, 10, 30)
    doc.text(`User ID: ${user.user_id || "—"}`, 10, 36)
  }
  doc.setTextColor("#555")
  doc.text(`Generated on ${today}`, pageWidth - 10, 30, { align: "right" })

  let currentY = 42

  // — Section 1: Subscription Payments —
  if (subxPayments && subxPayments.length) {
    doc.setFont("helvetica", "bold")
    doc.setFontSize(11)
    doc.setTextColor(brand)
    doc.text("Subscription Payments", 8, currentY)
    currentY += 6

    const subRows: RowInput[] = subxPayments.map((t) => {
      const status = computeStatus(t)
      const amount = formatMoney(t.amount)
      const method = formatPaymentMethod(t.payment_method, t.amount)
      const merchant = "ZOEE"

      return [
        t.date_processed ? dayjs(t.date_processed).format("M/D/YYYY") : "—",
        t.date_scheduled ? dayjs(t.date_scheduled).format("M/D/YYYY") : "—",
        merchant,
        t.paypal_promo_code || "—",
        method,
        status.label,
        amount,
      ]
    })

    autoTable(doc, {
      startY: currentY,
      head: [
        [
          "Processed",
          "Scheduled",
          "Merchant",
          "Promo Code",
          "Payment Method",
          "Status",
          "Amount",
        ],
      ],
      body: subRows,
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        textColor: [51, 51, 51],
        lineColor: [235, 235, 235],
        lineWidth: 0.2,
        valign: "middle",
      },
      headStyles: {
        fillColor: brandRgb,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [250, 250, 255] },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 5) {
          const rowIdx = data.row.index
          const t = subxPayments[rowIdx]
          const { hex } = computeStatus(t)
          data.cell.styles.textColor = hexToRgbArray(hex)
          data.cell.styles.fontStyle = "bold"
        }
        if (data.section === "body" && data.column.index === 3) {
          if (String(data.cell.raw).trim() === "—")
            data.cell.styles.textColor = [150, 150, 150]
        }
      },
      margin: { top: 42, right: 8, bottom: 16, left: 8 },
      pageBreak: "auto",
      tableWidth: "auto",
    })

    // Update Y
    // @ts-ignore
    currentY = (doc as any).lastAutoTable.finalY + 12
  }

  // — Section 2: Coach Purchases —
  if (coachPurchases && coachPurchases.length) {
    if (currentY < 42) currentY = 42
    doc.setFont("helvetica", "bold")
    doc.setFontSize(11)
    doc.setTextColor(brand)
    doc.text("Coach Purchases", 8, currentY)
    currentY += 6

    const coachRows: RowInput[] = coachPurchases.map((t) => {
      const status = computeStatus(t)
      const amount = formatMoney(t.amount)
      const method = formatPaymentMethod(t.payment_method, t.amount)
      const merchant = t.coach_name || t.merchant || "Coach"
      const serviceName =
        t.service_name || parseServiceTitle(t.service_details) || "Service"
      const promo = t.promo_code || "—"

      return [
        t.date_processed ? dayjs(t.date_processed).format("M/D/YYYY") : "—",
        t.date_scheduled ? dayjs(t.date_scheduled).format("M/D/YYYY") : "—",
        merchant,
        serviceName,
        promo,
        method,
        status.label,
        amount,
      ]
    })

    autoTable(doc, {
      startY: currentY,
      head: [
        [
          "Processed",
          "Scheduled",
          "Merchant",
          "Service",
          "Promo Code",
          "Payment Method",
          "Status",
          "Amount",
        ],
      ],
      body: coachRows,
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        textColor: [51, 51, 51],
        lineColor: [235, 235, 235],
        lineWidth: 0.2,
        valign: "middle",
      },
      headStyles: {
        fillColor: brandRgb,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [250, 250, 255] },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { cellWidth: 70 },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center" },
        7: { halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 6) {
          const rowIdx = data.row.index
          const t = coachPurchases[rowIdx]
          const { hex } = computeStatus(t)
          data.cell.styles.textColor = hexToRgbArray(hex)
          data.cell.styles.fontStyle = "bold"
        }
        if (data.section === "body" && data.column.index === 4) {
          if (String(data.cell.raw).trim() === "—")
            data.cell.styles.textColor = [150, 150, 150]
        }
      },
      margin: { top: 42, right: 8, bottom: 16, left: 8 },
      pageBreak: "auto",
      tableWidth: "auto",
    })
  }

  // Footer
  const poweredBy = "Powered by"
  const poweredByY = pageHeight - 8
  doc.setFont("helvetica", "normal")
  doc.setFontSize(8)
  doc.setTextColor("#9CA3AF")
  const textWidth = doc.getTextWidth(poweredBy)
  const logoW = 18
  const logoH = 7
  const spacing = 2
  const totalW = textWidth + spacing + logoW
  const startX = (pageWidth - totalW) / 2

  doc.text(poweredBy, startX, poweredByY)
  doc.addImage(
    logoBase64,
    "PNG",
    startX + textWidth + spacing,
    poweredByY - logoH + 1,
    logoW,
    logoH
  )

  doc.save(`${title} ${dayjs().format("YYYY-MM-DD")}.pdf`)
}

function parseServiceTitle(serviceDetails: any): string | null {
  if (!serviceDetails) return null
  try {
    const obj =
      typeof serviceDetails === "string"
        ? JSON.parse(serviceDetails)
        : serviceDetails
    return obj?.title || null
  } catch {
    return null
  }
}
